import { XCircle } from 'lucide-react';

/**
 * Generic Popover — pure presentational shell.
 *
 * Renders the popover box (title bar, close button, content slot) and the
 * directional arrow. All state management and data lookup live in the
 * wrapper components (CubePopover, TimelinePopover).
 *
 * Props:
 *   isClosing      {boolean}        — adds "closing" class for exit animation
 *   onClose        {function}       — called when the X button is clicked
 *   title          {string}         — text shown in the title bar
 *   anchor         {string}         — CSS position-anchor value (e.g. "--cube-0-0")
 *   positionArea   {string}         — CSS position-area value (e.g. "top" | "left")
 *   arrowPosition  {"bottom"|"right"} — which side the arrow sits on
 *   arrowOffset    {number}         — px offset from center for the arrow
 *   variant        {string}         — "cube" | "timeline" — adds a modifier class for variant-specific styling
 *   containerRef   {React.Ref}      — ref forwarded to the outer container div
 *   children       {ReactNode}      — wrapper-specific content inside popover-box
 */
export default function Popover({
  isClosing,
  onClose,
  title,
  anchor,
  positionArea,
  arrowPosition = "bottom",
  arrowOffset = 0,
  variant,
  containerRef,
  children,
}) {
  const arrowStyle =
    arrowPosition === "bottom"
      ? { left: `calc(50% + ${arrowOffset}px)` }
      : { top: `calc(50% + ${arrowOffset}px)` };

  const variantClass = variant ? `popover-container--${variant}` : "";

  return (
    <div
      ref={containerRef}
      className={`popover-container ${variantClass} ${isClosing ? "closing" : ""}`}
      style={{ positionAnchor: anchor, positionArea }}
    >
      <div className="popover-box">
        <div className="popover-title-bar">{title}</div>

        <button
          className="popover-close-btn"
          onClick={onClose}
          aria-label="Close info"
        >
          <XCircle size={28} />
        </button>

        <div className="popover-content">
          {children}
        </div>
      </div>

      <div
        className={`popover-arrow popover-arrow--${arrowPosition}`}
        style={arrowStyle}
      />
    </div>
  );
}
